import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import Iconify from '../Iconify';
import { CHIP_GREEN, CHIP_AMBER, CHIP_GREY } from './actionCardColors';
import { CHIP } from './cardTypography';

/**
 * Period-over-period change pill (the "Your pipeline" TrendChip look): arrow + "+x.x%".
 *
 * Colour rule — red is reserved for overdue/late, so a bad trend is AMBER, never red:
 *   good  → CHIP_GREEN      bad → CHIP_AMBER      zero / unknown → grey "—"
 * `inverted` flips which direction is good, for cost-type metrics (spend, lost leads).
 */
const TrendPill = ({ value = null, inverted = false, tooltip = undefined, sx = undefined }) => {
    const known = typeof value === 'number' && Number.isFinite(value);
    const isFlat = !known || value === 0;
    const isUp = known && value > 0;
    // good = the favourable direction (flipped for cost-type metrics)
    const isGood = inverted ? !isUp : isUp;
    let tone = CHIP_GREY;
    if (!isFlat) tone = isGood ? CHIP_GREEN : CHIP_AMBER;
    let text = '—';
    if (!isFlat) text = `${isUp ? '+' : '-'}${Math.abs(value).toFixed(1)}%`;
    let ariaLabel = 'No previous period comparison available';
    if (known && value === 0) ariaLabel = 'Unchanged versus previous period';
    if (!isFlat) ariaLabel = `${isUp ? 'Increased' : 'Decreased'} ${Math.abs(value).toFixed(1)} percent versus previous period`;
    return (
        <Box
            component="span"
            data-testid="trend-pill"
            aria-label={ariaLabel}
            title={tooltip}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                ...CHIP,
                borderRadius: '999px',
                bgcolor: tone.bg,
                color: tone.text,
                ...sx,
            }}
        >
            {!isFlat && (
                <Iconify icon={isUp ? 'eva:trending-up-fill' : 'eva:trending-down-fill'} width={13} height={13} />
            )}
            {text}
        </Box>
    );
};

TrendPill.propTypes = {
    value: PropTypes.number,
    inverted: PropTypes.bool,
    tooltip: PropTypes.string,
    sx: PropTypes.object,
};

export default TrendPill;
